import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export interface ConversationMessage {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export interface Conversation {
  id: string;
  userId: number;
  title: string;
  dataFilter?: string;
  createdAt: string;
  updatedAt: string;
  messages?: ConversationMessage[];
}

export interface SendMessageRequest {
  message: string;
  conversationId?: string;
  dataFilter?: any;
  modelId?: string;
  requestType?: string;
}

export interface SendMessageResponse {
  conversationId: string;
  messageId: string;
  content: string;
  role: 'assistant';
  createdAt: string;
}

class ConversationService {
  // Get auth token from localStorage
  private getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  // Get auth headers
  private getAuthHeaders() {
    const token = this.getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Send a message in a conversation
  async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/conversations/message`,
        request,
        { headers: this.getAuthHeaders() }
      );

      // Handle direct response format (not wrapped in success/data)
      if (response.data.conversationId && response.data.messageId) {
        return response.data;
      } else if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to send message');
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      // Check for detailed error message first, then fallback to generic error
      const errorMessage = error.response?.data?.details || error.response?.data?.error || 'Failed to send message';
      throw new Error(errorMessage);
    }
  }

  // Get conversation history
  async getConversation(conversationId: string): Promise<Conversation> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/conversations/${conversationId}`,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to get conversation');
      }
    } catch (error: any) {
      console.error('Error getting conversation:', error);
      throw new Error(error.response?.data?.error || 'Failed to get conversation');
    }
  }

  // Get user conversations
  async getUserConversations(): Promise<Conversation[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/conversations`,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to get conversations');
      }
    } catch (error: any) {
      console.error('Error getting user conversations:', error);
      throw new Error(error.response?.data?.error || 'Failed to get conversations');
    }
  }

  // Delete a conversation
  async deleteConversation(conversationId: string): Promise<void> {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/conversations/${conversationId}`,
        { headers: this.getAuthHeaders() }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete conversation');
      }
    } catch (error: any) {
      console.error('Error deleting conversation:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete conversation');
    }
  }

  // Update conversation title
  async updateConversationTitle(conversationId: string, title: string): Promise<void> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/conversations/${conversationId}/title`,
        { title },
        { headers: this.getAuthHeaders() }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to update conversation title');
      }
    } catch (error: any) {
      console.error('Error updating conversation title:', error);
      throw new Error(error.response?.data?.error || 'Failed to update conversation title');
    }
  }
}

export default new ConversationService(); 